/**
 * The primitives every painter draws with: palettes, tile-repeating noise,
 * tone ramps and pixel-crisp shapes. Kept apart from the atlas so the art
 * modules it paints from (shipArt, plantArt) can use them without an import cycle.
 */

/** Pixels per tile in the atlas. Drawn procedurally; see atlasTexture for how it's sampled. */
export const TILE_PX = 32;
export const S = TILE_PX;

export type Ctx = CanvasRenderingContext2D;

/**
 * Every block is painted this many times, one atlas slot each, and each tile
 * picks one from where it is (see GridMesh), so a big rock face or hull isn't
 * one tile stamped over and over. Painters that don't care about the variant
 * paint the same each time.
 */
export const VARIANTS = 3;

// ----- palettes, dark to light

export const WOOD = ["#3a2412", "#5e3c1d", "#8a5a2e", "#a8733f", "#c48a4e"] as const;
export const WALL = ["#1d130b", "#2a1c10", "#382616", "#45301c", "#523a23"] as const;
export const IRON = ["#24272b", "#3c4046", "#5a5e65", "#787c83", "#a2a6ad"] as const;
export const BRASS = ["#5e3e16", "#8a6228", "#b0813a", "#d4a253", "#f0d090"] as const;
export const GLASS = ["#3f6476", "#6d93a6", "#8fb3c4", "#b5d3df", "#e6f6fb"] as const;
export const ROPE = ["#6e5a3a", "#9a8058", "#c8b088", "#e2d0aa"] as const;
export const FIRE = ["#7a1e08", "#c8400e", "#ff8a2a", "#ffc25a", "#fff0b8"] as const;
export const ROCK = ["#3b332b", "#53483d", "#6b5e4e", "#857663", "#9d8d77"] as const;
export const TEAL = ["#0c2e2c", "#14423f", "#1d5e5a", "#2a8a7e", "#38a896", "#7fe0c8"] as const;
export const STARBARK = ["#151026", "#1f1838", "#2c2448", "#3a3060", "#4a3e78"] as const;

// ----- colour

export type Rgb = readonly [number, number, number];
export const rgbs = new Map<string, Rgb>();
export function rgb(hex: string): Rgb {
  let c = rgbs.get(hex);
  if (!c) rgbs.set(hex, (c = [parseInt(hex.slice(1, 3), 16), parseInt(hex.slice(3, 5), 16), parseInt(hex.slice(5, 7), 16)]));
  return c;
}

// ----- noise: all of it repeats every tile, so a texture meets itself seamlessly in the next

export function hash(x: number, y: number, seed: number): number {
  let h = Math.imul(x, 374761393) + Math.imul(y, 668265263) + Math.imul(seed, 982451653);
  h = Math.imul(h ^ (h >>> 13), 1274126177);
  return ((h ^ (h >>> 16)) >>> 0) / 4294967296;
}

export const wrap = (a: number, n: number) => ((a % n) + n) % n;
/** One turn per tile: for waves that repeat every tile. */
export const TURN = (Math.PI * 2) / S;

/** Smooth value noise, 0..1, with features `cell` pixels apart (a divisor of S). */
export function noise(x: number, y: number, cell: number, seed: number): number {
  const n = S / cell;
  const gx = x / cell;
  const gy = y / cell;
  const ix = Math.floor(gx);
  const iy = Math.floor(gy);
  const fx = gx - ix;
  const fy = gy - iy;
  const sx = fx * fx * (3 - 2 * fx);
  const sy = fy * fy * (3 - 2 * fy);
  const h = (i: number, j: number) => hash(wrap(i, n), wrap(j, n), seed);
  const top = h(ix, iy) + (h(ix + 1, iy) - h(ix, iy)) * sx;
  const bottom = h(ix, iy + 1) + (h(ix + 1, iy + 1) - h(ix, iy + 1)) * sx;
  return top + (bottom - top) * sy;
}

export interface Stone {
  /** How far the pixel is from the seam with the next stone (pixels, roughly). */
  edge: number;
  /** This stone's own random, 0..1. */
  tone: number;
  /** Where in the stone the pixel is, about -1..1 from its middle. */
  dx: number;
  dy: number;
}

/** Stones packed together (cellular noise), about `cell` pixels across (a divisor of S). */
export function stoneAt(x: number, y: number, cell: number, seed: number): Stone {
  const n = S / cell;
  const px = x + 0.5;
  const py = y + 0.5;
  const ix = Math.floor(px / cell);
  const iy = Math.floor(py / cell);
  let d1 = Infinity;
  let d2 = Infinity;
  let tone = 0;
  let cx = 0;
  let cy = 0;
  for (let j = iy - 1; j <= iy + 1; j++) {
    for (let i = ix - 1; i <= ix + 1; i++) {
      const wi = wrap(i, n);
      const wj = wrap(j, n);
      const fx = (i + 0.15 + hash(wi, wj, seed) * 0.7) * cell;
      const fy = (j + 0.15 + hash(wi, wj, seed + 1) * 0.7) * cell;
      const d = Math.hypot(px - fx, py - fy);
      if (d < d1) {
        d2 = d1;
        d1 = d;
        tone = hash(wi, wj, seed + 2);
        cx = fx;
        cy = fy;
      } else if (d < d2) d2 = d;
    }
  }
  return { edge: d2 - d1, tone, dx: (px - cx) / cell, dy: (py - cy) / cell };
}

/** 4x4 ordered dither: blends neighbouring tones in a pixel pattern rather than a smooth gradient. */
export const BAYER = [0, 8, 2, 10, 12, 4, 14, 6, 3, 11, 1, 9, 15, 7, 13, 5].map((v) => (v + 0.5) / 16 - 0.5);

/** The tone of `ramp` (dark to light) for a lightness `k` (0..1) at pixel (x, y), dithered between neighbours. */
export function tone(ramp: readonly string[], k: number, x: number, y: number, dither = 0.6): string {
  const i = Math.floor(k * ramp.length + BAYER[(y & 3) * 4 + (x & 3)]! * dither);
  return ramp[Math.max(0, Math.min(ramp.length - 1, i))]!;
}

/** Wavy streaks of grain running along `along`, a few pixels apart across it: a lightness to add. */
export function grain(along: number, across: number, seed: number): number {
  const s = Math.sin(across * 2.1 + noise(along, across, 8, seed) * 6);
  return s > 0.72 ? -0.22 : s < -0.9 ? 0.08 : 0;
}

// ----- drawing, pixel-crisp (no antialiased canvas paths, which smear at this size)

export type PixelFn = (x: number, y: number) => string | null;

let scratch: HTMLCanvasElement | undefined;
/** Paint a w x h area at (x0, y0) pixel by pixel: `color` gives each one, or null to leave it. */
export function pixels(ctx: Ctx, w: number, h: number, color: PixelFn, x0 = 0, y0 = 0): void {
  scratch ??= Object.assign(document.createElement("canvas"), { width: S * 4, height: S * 4 });
  const g = scratch.getContext("2d")!;
  const img = g.createImageData(w, h);
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      const c = color(x, y);
      if (!c) continue;
      const [r, gr, b] = rgb(c);
      const i = (y * w + x) * 4;
      img.data[i] = r;
      img.data[i + 1] = gr;
      img.data[i + 2] = b;
      img.data[i + 3] = 255;
    }
  }
  g.putImageData(img, 0, 0);
  ctx.drawImage(scratch, 0, 0, w, h, x0, y0, w, h);
}

export function fill(ctx: Ctx, color: string, x = 0, y = 0, w = S, h = S): void {
  ctx.fillStyle = color;
  ctx.fillRect(x, y, w, h);
}

/** Fill every pixel in the box [x0, x1) x [y0, y1) whose middle is `inside`, a run at a time. */
export function shape(ctx: Ctx, color: string, x0: number, y0: number, x1: number, y1: number, inside: (x: number, y: number) => boolean): void {
  ctx.fillStyle = color;
  const [l, r] = [Math.floor(x0), Math.ceil(x1)];
  for (let y = Math.floor(y0); y < Math.ceil(y1); y++) {
    let run = -1;
    for (let x = l; x <= r; x++) {
      const hit = x < r && inside(x + 0.5, y + 0.5);
      if (hit && run < 0) run = x;
      else if (!hit && run >= 0) {
        ctx.fillRect(run, y, x - run, 1);
        run = -1;
      }
    }
  }
}

export function disc(ctx: Ctx, color: string, cx: number, cy: number, r: number): void {
  shape(ctx, color, cx - r, cy - r, cx + r, cy + r, (x, y) => (x - cx) ** 2 + (y - cy) ** 2 <= r * r);
}

export function ring(ctx: Ctx, color: string, cx: number, cy: number, r0: number, r1: number): void {
  shape(ctx, color, cx - r1, cy - r1, cx + r1, cy + r1, (x, y) => {
    const d = (x - cx) ** 2 + (y - cy) ** 2;
    return d >= r0 * r0 && d <= r1 * r1;
  });
}

/** An ellipse `rx` x `ry` turned `angle`. */
export function oval(ctx: Ctx, color: string, cx: number, cy: number, rx: number, ry: number, angle = 0): void {
  const [c, s] = [Math.cos(angle), Math.sin(angle)];
  const r = Math.max(rx, ry);
  shape(ctx, color, cx - r, cy - r, cx + r, cy + r, (x, y) => {
    const u = (x - cx) * c + (y - cy) * s;
    const v = -(x - cx) * s + (y - cy) * c;
    return (u / rx) ** 2 + (v / ry) ** 2 <= 1;
  });
}

/** A stroke `w` wide from (x0, y0) to (x1, y1), square ended. */
export function line(ctx: Ctx, color: string, x0: number, y0: number, x1: number, y1: number, w = 1): void {
  const dx = x1 - x0;
  const dy = y1 - y0;
  const l2 = dx * dx + dy * dy || 1;
  const r = w / 2;
  shape(ctx, color, Math.min(x0, x1) - r, Math.min(y0, y1) - r, Math.max(x0, x1) + r, Math.max(y0, y1) + r, (x, y) => {
    const t = Math.max(0, Math.min(1, ((x - x0) * dx + (y - y0) * dy) / l2));
    return Math.abs(x - x0 - t * dx) <= r && Math.abs(y - y0 - t * dy) <= r;
  });
}

/** Strokes through the points [x0, y0, x1, y1, ...]. */
export function path(ctx: Ctx, color: string, pts: readonly number[], w = 1): void {
  for (let k = 2; k + 1 < pts.length; k += 2) line(ctx, color, pts[k - 2]!, pts[k - 1]!, pts[k]!, pts[k + 1]!, w);
}

/** A filled polygon through the points [x0, y0, x1, y1, ...]. */
export function poly(ctx: Ctx, color: string, pts: readonly number[]): void {
  const xs = pts.filter((_, i) => i % 2 === 0);
  const ys = pts.filter((_, i) => i % 2 === 1);
  shape(ctx, color, Math.min(...xs), Math.min(...ys), Math.max(...xs), Math.max(...ys), (x, y) => {
    let inside = false;
    for (let i = 0, j = xs.length - 1; i < xs.length; j = i++) {
      if (ys[i]! > y !== ys[j]! > y && x < ((xs[j]! - xs[i]!) * (y - ys[i]!)) / (ys[j]! - ys[i]!) + xs[i]!) inside = !inside;
    }
    return inside;
  });
}

/** A raised rectangle: `face`, a `light` top and left edge and a `dark` bottom and right. */
export function panel(ctx: Ctx, x: number, y: number, w: number, h: number, face: string, light: string, dark: string): void {
  fill(ctx, face, x, y, w, h);
  fill(ctx, light, x, y, w, 1);
  fill(ctx, light, x, y, 1, h);
  fill(ctx, dark, x, y + h - 1, w, 1);
  fill(ctx, dark, x + w - 1, y, 1, h);
}

/** An iron bar or plate. */
export const plate = (ctx: Ctx, x: number, y: number, w: number, h: number) => panel(ctx, x, y, w, h, IRON[2], IRON[3], IRON[1]);
/** A brass band or fitting. */
export const brass = (ctx: Ctx, x: number, y: number, w: number, h: number) => panel(ctx, x, y, w, h, BRASS[2], BRASS[4], BRASS[0]);

/** A beam of timber, its grain along its length, lit along the top (or left) and shadowed along the bottom (or right). */
export function beam(ctx: Ctx, x0: number, y0: number, w: number, h: number, ramp: readonly string[] = WOOD): void {
  const across = w >= h;
  pixels(
    ctx,
    w,
    h,
    (x, y) => {
      const [a, b, n] = across ? [y, x, h] : [x, y, w];
      let k = 0.55 + grain(b, a, x0 * 7 + y0);
      if (a === 0) k = 0.88;
      else if (a === n - 1) k = 0.12;
      else if (a === n - 2 && n > 4) k -= 0.18;
      return tone(ramp, k, x, y, 0.4);
    },
    x0,
    y0,
  );
}

/** Planks side by side over a w x h area, each `wide` pixels: running down if `upright`, else across. */
export function planks(ctx: Ctx, x0: number, y0: number, w: number, h: number, wide: number, upright: boolean, seed: number, ramp: readonly string[] = WOOD): void {
  pixels(
    ctx,
    w,
    h,
    (x, y) => {
      const [a, b] = upright ? [x, y] : [y, x];
      const i = Math.floor(a / wide);
      const c = a - i * wide;
      if (c === wide - 1) return ramp[0]!;
      let k = 0.5 + (hash(i, 0, seed) - 0.5) * 0.25 + grain(b, a, seed + i);
      if (c === 0) k += 0.28;
      else if (c === wide - 2) k -= 0.2;
      return tone(ramp, k, x, y, 0.45);
    },
    x0,
    y0,
  );
}

/** A round-headed rivet, lit from the upper left, with its shadow. */
export function rivet(ctx: Ctx, x: number, y: number): void {
  fill(ctx, IRON[0], x + 1, y + 1, 2, 2);
  fill(ctx, IRON[3], x, y, 2, 2);
  fill(ctx, IRON[4], x, y, 1, 1);
}

export function nail(ctx: Ctx, x: number, y: number): void {
  fill(ctx, "#1e1e20", x, y, 2, 2);
  fill(ctx, "#7a7d82", x, y, 1, 1);
}

/** A sheet of glass: darker toward the bottom, two streaks of reflection across it. */
export function glassPane(ctx: Ctx, x0: number, y0: number, w: number, h: number): void {
  pixels(
    ctx,
    w,
    h,
    (x, y) => {
      const d = x + y - (w + h) * 0.3;
      if ((d >= 0 && d < 3) || (d >= 5 && d < 6)) return GLASS[4];
      return tone(GLASS, 0.8 - (y / h) * 0.5, x, y, 0.8);
    },
    x0,
    y0,
  );
}

/**
 * Where a feature goes on variant `v` of a tile: as drawn (0), mirrored
 * left-right (1), or turned half round (2). For textures with no up or down.
 */
export function at(v: number, x: number, y: number, w = 1, h = 1): [number, number] {
  return [v >= 1 ? S - x - w : x, v === 2 ? S - y - h : y];
}

/** A point (not a pixel) moved as `at` moves features. */
export const flip = (v: number, x: number, y: number): [number, number] => [v >= 1 ? S - x : x, v === 2 ? S - y : y];

// ----- natural ground

export interface StoneLook {
  /** How wide the dark seams between stones are. */
  seam?: number;
  /** How much fine grit there is over everything. */
  grit?: number;
  /** How rounded the stones look (lit on their upper left). */
  relief?: number;
}

/** Stones packed in a field (see stoneAt), each lit from the upper left, with dark seams between them. */
export function stoneField(ramp: readonly string[], cell: number, seed: number, { seam = 1.1, grit = 0.2, relief = 0.22 }: StoneLook = {}): PixelFn {
  return (x, y) => {
    const s = stoneAt(x, y, cell, seed);
    if (s.edge < seam) return ramp[0]!;
    let k = 0.45 + (s.tone - 0.5) * 0.35 - (s.dx + s.dy) * relief + (noise(x, y, 4, seed + 9) - 0.5) * grit;
    if (s.edge < seam + 1.2) k += s.dx + s.dy < 0 ? 0.2 : -0.15;
    return tone(ramp, k, x, y);
  };
}

// ----- built blocks

/** Dark upright boards; the back wall of every interior, and behind ladders, hatches and helms. */
export function backWall(ctx: Ctx, v = 0): void {
  pixels(ctx, S, S, (x, y) => {
    const board = x >> 3;
    const c = x & 7;
    if (c === 7) return WALL[0];
    let k = 0.5 + (hash(board, v, 41) - 0.5) * 0.3 + grain(y, x, 43 + board) * 0.8;
    if (c === 0) k += 0.22;
    else if (c === 6) k -= 0.18;
    return tone(WALL, k, x, y, 0.5);
  });
}

/** Back wall boards over a whole `w` x `h` tile piece, for those that stand in front of one. */
export function backWalls(ctx: Ctx, w: number, h: number): void {
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      ctx.save();
      ctx.translate(x * S, y * S);
      backWall(ctx, (x + y) % VARIANTS);
      ctx.restore();
    }
  }
}

/**
 * Give what's drawn over a clear background a dark outline, the way hand-made
 * pixel art is: each clear pixel next to a drawn one becomes a deep shade of it.
 */
export function outline(canvas: HTMLCanvasElement): void {
  const ctx = canvas.getContext("2d", { willReadFrequently: true })!;
  const { width: w, height: h } = canvas;
  const img = ctx.getImageData(0, 0, w, h);
  const src = img.data.slice();
  const solid = (x: number, y: number) => x >= 0 && y >= 0 && x < w && y < h && src[(y * w + x) * 4 + 3]! >= 128;
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      if (solid(x, y)) continue;
      // Below first: the outline under a shape takes its darkest side's colour.
      const n = [[0, 1], [1, 0], [-1, 0], [0, -1]].find(([dx, dy]) => solid(x + dx!, y + dy!));
      if (!n) continue;
      const i = (y * w + x) * 4;
      const j = ((y + n[1]!) * w + x + n[0]!) * 4;
      for (let c = 0; c < 3; c++) img.data[i + c] = src[j + c]! * 0.32;
      img.data[i + 3] = 255;
    }
  }
  ctx.putImageData(img, 0, 0);
}
